<template>
    <div class="flex h-full min-w-0">
        <!-- Month Grid -->
        <div class="flex-grow grid grid-cols-7 gap-px bg-gray-200 dark:bg-gray-700 overflow-hidden border-b border-r border-gray-200 min-w-0">
            <!-- Day Headers -->
            <div 
                v-for="day in weekDays" 
                :key="day" 
                class="bg-white dark:bg-gray-800 p-3 text-center"
            >
                <div class="text-sm font-medium text-gray-500 dark:text-gray-400 ">
                    {{ day }}
                </div>
            </div>
            
            <!-- Calendar Days -->
            <div 
                v-for="date in calendarDays" 
                :key="date.toISOString()"
                class="bg-white dark:bg-gray-800 min-h-28 p-2 relative min-w-0 overflow-hidden"
                :class="{
                    'bg-gray-50 dark:bg-gray-700': !isCurrentMonth(date),
                    'bg-indigo-50 dark:bg-indigo-900/20': isToday(date)
                }"
            >
                <!-- Date Number -->
                <div class="flex justify-between items-start">
                    <span 
                        class="text-sm font-medium"
                        :class="{
                            'text-gray-400': !isCurrentMonth(date),
                            'text-indigo-600 dark:text-indigo-400': isToday(date),
                            'text-gray-900 dark:text-white': isCurrentMonth(date) && !isToday(date)
                        }"
                    >
                        {{ date.getDate() }}
                    </span>
                    <button 
                        v-if="isCurrentMonth(date) && !isPastDate(date)"
                        @click="addEvent(date)"
                        class="cursor-pointer group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full"
                    >
                        <PlusIcon class="size-4 text-green-500" />
                    </button>
                </div>
                
                <!-- Events for this day -->
                <div class="space-y-1">
                    <div 
                        v-for="event in getEventsForDate(date).slice(0, 3)" 
                        :key="event.id"
                        @click="selectEvent(event)"
                        class="text-xs p-1 rounded cursor-pointer truncate"
                        :class="getEventClasses(event)"
                        :style="getEventStyles(event)"
                        :title="event.title"
                    >
                        {{ event.title }}
                    </div>
                    <!-- See more button when there are more than 3 events -->
                    <button 
                        v-if="getEventsForDate(date).length > 3"
                        @click="seeMoreEvents(date)"
                        class="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium w-full text-left p-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors border border-indigo-200 dark:border-indigo-700 hover:border-indigo-300 dark:hover:border-indigo-600"
                    >
                        +{{ getEventsForDate(date).length - 3 }} more
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import { PlusIcon } from '@heroicons/vue/24/outline'
import { isTailwindColor } from '@/utilities/color'
import { useI18n } from 'vue-i18n'

// Props
const props = defineProps({
    currentDate: {
        type: Date,
        default: () => new Date()
    },
    events: {
        type: Array,
        default: () => []
    },
    categories: {
        type: Array,
        default: () => []
    }
})

// Emits
const emit = defineEmits(['event-select', 'add-event', 'see-more'])

const { locale } = useI18n()

// Week days
const weekDays = computed(() => {
    return Array.from({ length: 7 }).map((_, i) => {
        // Jan 4, 1970 was a Sunday
        const d = new Date(1970, 0, 4 + i)
        return d.toLocaleDateString(locale.value, { weekday: 'short' })
    })
})

// Computed properties
const calendarDays = computed(() => {
    const year = props.currentDate.getFullYear()
    const month = props.currentDate.getMonth()
    const firstDay = new Date(year, month, 1)
    const startDate = new Date(firstDay)
    startDate.setDate(startDate.getDate() - firstDay.getDay())
    
    const days = []
    for (let i = 0; i < 42; i++) {
        const date = new Date(startDate)
        date.setDate(startDate.getDate() + i)
        days.push(date)
    }
    return days
})

// Methods
const isCurrentMonth = (date) => {
    return date.getMonth() === props.currentDate.getMonth()
}

const isToday = (date) => {
    const today = new Date()
    return date.toDateString() === today.toDateString()
}

const isPastDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
}

const getEventsForDate = (date) => {
    const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
    const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999)
    return props.events.filter(event => {
        const eventStart = new Date(event.startTime || event.date)
        const eventEnd = event.endTime ? new Date(event.endTime) : eventStart
        return eventStart.getTime() <= dayEnd.getTime() && eventEnd.getTime() > dayStart.getTime()
    })
}

const getEventStyles = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    
    if (isTailwindColor(color)) {
        return {}
    }
    return {
        backgroundColor: color
    }
}

const getEventClasses = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    const baseClasses = 'text-white text-xs font-medium'
    
    if (isTailwindColor(color)) {
        return `${baseClasses} bg-${color}-500 hover:bg-${color}-600`
    }
    return baseClasses
}

const selectEvent = (event) => {
    emit('event-select', event)
}

const addEvent = (date) => {
    emit('add-event', { date, time: null })
}

const seeMoreEvents = (date) => {
    emit('see-more', { date, events: getEventsForDate(date) })
}
</script>
